import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router';
import { selectAccessToken, selectJustLoggedOut } from '../../../features/auth/auth-slice';
import usePersist from '../../../hooks/common/use-persist';
import { useRefreshTokenMutation } from '../../../api/auth/auth-api';
import { authSession } from '../../../helper/auth/auth-session';
import LoadingAuth from '../../../components/ui/loading-auth';

export default function PersistLogin() {
    const [persist] = usePersist();
    const token = useSelector(selectAccessToken);
    const justLoggedOut = useSelector(selectJustLoggedOut);
    const [isRefreshed, setIsRefreshed] = useState(false);
    const [refreshToken, { isLoading }] = useRefreshTokenMutation();

    useEffect(() => {
        let isMounted = true;
        const verifyRefreshToken = async () => {
            // If we already have a token, just mark as refreshed
            if (token) {
                if (isMounted) setIsRefreshed(true);
                return;
            }

            // If user explicitly logged out or persist is disabled, do not refresh
            if (justLoggedOut || !persist) {
                if (isMounted) setIsRefreshed(true);
                return;
            }

            // If there is no previous session recorded at all, do NOT call the refresh API.
            // This prevents unauthenticated direct hits to protected routes from firing 401 API calls.
            const hasPreviousSession = authSession.hasSession();
            if (!hasPreviousSession) {
                if (isMounted) setIsRefreshed(true);
                return;
            }

            try {
                await refreshToken().unwrap();
                if (isMounted) setIsRefreshed(true);
            } catch (error) {
                authSession.clearSession();
                if (isMounted) setIsRefreshed(true);
            }
        };

        verifyRefreshToken();

        return () => {
            isMounted = false;
        };
    }, [token, persist, justLoggedOut, refreshToken]);

    if (!isRefreshed || isLoading) {
        return <LoadingAuth />;
    }

    return <Outlet />;
}